import { useRef, useState } from "react";
import type { TestEmailResult } from "@pustak-lms/contracts";
import { ApiRequestError } from "@pustak-lms/api-client";
import { Button, PageHeader } from "@pustak-lms/ui";
import { platformApiClient } from "./api";

const recipient = "medicalb2blms@gmail.com";

type SendState =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "accepted"; result: TestEmailResult }
  | { kind: "failed"; message: string }
  | { kind: "disabled" };

export function TestingPage() {
  const [state, setState] = useState<SendState>({ kind: "idle" });
  const pendingRef = useRef(false);

  async function send() {
    if (pendingRef.current || state.kind === "disabled") return;
    pendingRef.current = true;
    setState({ kind: "pending" });
    try {
      const result = await platformApiClient().sendTestEmail();
      setState({ kind: "accepted", result });
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "EMAIL_TEST_DISABLED") {
        setState({ kind: "disabled" });
      } else if (error instanceof ApiRequestError && error.code === "EMAIL_SEND_UNCONFIRMED") {
        setState({
          kind: "failed",
          message: "Email acceptance could not be confirmed. Check the inbox or Resend before retrying to avoid a duplicate."
        });
      } else if (error instanceof ApiRequestError && error.code !== "REQUEST_FAILED") {
        setState({ kind: "failed", message: error.message });
      } else {
        setState({
          kind: "failed",
          message: "The request outcome could not be confirmed. Check the inbox or Resend before retrying to avoid a duplicate."
        });
      }
    } finally {
      pendingRef.current = false;
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Internal operations"
        title="Testing"
        description="Send one fixed sample email to check the Worker and Resend integration."
      />
      <section className="panel console-testing-panel" aria-labelledby="test-email-heading">
        <h2 id="test-email-heading">Sample email</h2>
        <p className="console-muted">Recipient: <strong>{recipient}</strong></p>
        <p className="console-muted">The message uses a fixed sender, subject, and content. Resend acceptance does not confirm inbox delivery.</p>
        <Button onClick={() => { void send(); }} disabled={state.kind === "pending" || state.kind === "disabled"}>
          {state.kind === "pending" ? "Sending…" : "Send test email"}
        </Button>
        {state.kind === "pending" ? <p role="status">Sending test email…</p> : null}
        {state.kind === "accepted" ? (
          <div className="console-alert console-alert-success" role="status">
            <p>Resend accepted the test email for {state.result.to}. Check the inbox separately for delivery.</p>
            <p>Provider message ID: <strong>{state.result.id}</strong></p>
          </div>
        ) : null}
        {state.kind === "failed" ? <p className="console-alert console-alert-error" role="alert">{state.message}</p> : null}
        {state.kind === "disabled" ? (
          <p className="console-alert console-alert-error" role="alert">Email testing is unavailable in this environment.</p>
        ) : null}
      </section>
    </>
  );
}
